import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Collapse,
  DatePicker,
  Descriptions,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Upload,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  UploadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import * as dayjs from "dayjs";
import { useFirestore } from "react-redux-firebase";
import { useTranslation } from "react-i18next";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";

import {
  FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE,
  FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { formatPriceByCurrency } from "../../../../../functions/formatters";
import urls from "../../../../../urls";
import { useSelector } from "react-redux";
import {
  useProducts,
  usePurchaseOrderInvoice,
  usePurchaseOrderInvoices,
  usePurchaseOrderPayments,
  usePurchaseOrders,
  usePurchaseOrderShipments,
  useSuppliers,
} from "../../../../../data/hooks";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";

const { TextArea } = Input;
const { Panel } = Collapse;

const invoiceStatusColors = {
  draft: "default",
  submitted: "processing",
  approved: "blue",
  partially_paid: "orange",
  paid: "green",
  disputed: "red",
};

const invoiceStatusLabels = {
  draft: "Concept",
  submitted: "Ingediend",
  approved: "Goedgekeurd",
  partially_paid: "Deels betaald",
  paid: "Betaald",
  disputed: "Betwist",
};

const PurchaseOrderInvoiceDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const [saving, setSaving] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [paymentDate, setPaymentDate] = useState(dayjs());
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [uploadingPaymentProof, setUploadingPaymentProof] = useState(false);
  const [paymentExchangeRate, setPaymentExchangeRate] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [savingCorrection, setSavingCorrection] = useState(false);
  const [correctionAmount, setCorrectionAmount] = useState(null);
  const [correctionIsNegative, setCorrectionIsNegative] = useState(false);
  const [correctionDate, setCorrectionDate] = useState(dayjs());
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionReference, setCorrectionReference] = useState("");
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [editingInvoiceNumber, setEditingInvoiceNumber] = useState(false);
  const [invoiceNumberValue, setInvoiceNumberValue] = useState("");
  const [savingInvoiceNumber, setSavingInvoiceNumber] = useState(false);
  const [editingInvoiceDate, setEditingInvoiceDate] = useState(false);
  const [invoiceDateValue, setInvoiceDateValue] = useState(null);
  const [savingInvoiceDate, setSavingInvoiceDate] = useState(false);
  const [editingTotalAmount, setEditingTotalAmount] = useState(false);
  const [totalAmountValue, setTotalAmountValue] = useState(null);
  const [savingTotalAmount, setSavingTotalAmount] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [editedInvoiceLines, setEditedInvoiceLines] = useState([]);
  const [editedAdditionalCosts, setEditedAdditionalCosts] = useState([]);
  const [editedDiscountLines, setEditedDiscountLines] = useState([]);
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newLineQuantity, setNewLineQuantity] = useState(1);
  const [newLineUnitPrice, setNewLineUnitPrice] = useState(0);
  const [newLineDiscount, setNewLineDiscount] = useState(0);
  const [legacyPayments, setLegacyPayments] = useState([]);
  const [syncingToExact, setSyncingToExact] = useState(false);
  const email = useSelector((state) => state.firebaseReducer.auth.email);

  // Use hooks for invoices, shipments, payments, and purchase orders
  const { invoices: allInvoices } = usePurchaseOrderInvoices();
  const { invoice, loading: invoiceLoading, invoiceHistory, historyLoading } = usePurchaseOrderInvoice(id);
  const { shipments: allShipments } = usePurchaseOrderShipments();
  const { payments } = usePurchaseOrderPayments({ invoiceId: id });
  const { purchaseOrders } = usePurchaseOrders();
  const { suppliers } = useSuppliers();

  // Find the shipment by invoice.shipmentId
  const shipment = allShipments.find((ship) => ship.id === invoice?.shipmentId);
  const order = purchaseOrders.find((order) => order.id === invoice?.purchaseOrderId);

  // Get current supplier to extract productIds
  const currentSupplier = suppliers.find((s) => s.uid === shipment?.supplierId);
  const { products } = useProducts({ productIds: currentSupplier?.productIds || [] });

  // Use invoice's own currency
  const invoiceCurrency = invoice?.currency || "USD";

  // Determine if invoice is out of sync with Exact (compare last history subcollection entry with exactSyncedAt)
  const exactOutOfSync = useMemo(() => {
    if (!invoice?.exactPurchaseInvoiceId || !invoice?.exactSyncedAt) return false;
    if (!invoiceHistory || invoiceHistory.length === 0) return false;

    const lastHistoryEntry = invoiceHistory[invoiceHistory.length - 1];
    return lastHistoryEntry.changedAt > invoice.exactSyncedAt;
  }, [invoice, invoiceHistory]);

  // Load legacy payments from purchase orders using hook data
  useEffect(() => {
    if (!shipment?.sourceOrders || !purchaseOrders?.length) return;

    const allLegacyPayments = [];
    const orderIds = shipment.sourceOrders.map((so) => so.purchaseOrderId).filter(Boolean);

    for (const orderId of orderIds) {
      const order = purchaseOrders.find((po) => po.id === orderId);
      if (order && order.payments && Array.isArray(order.payments)) {
        for (const payment of order.payments) {
          allLegacyPayments.push({
            ...payment,
            sourceOrderNumber: order.orderNumber,
            sourceOrderId: orderId,
            isLegacy: true,
          });
        }
      }
    }

    setLegacyPayments(allLegacyPayments);
  }, [shipment, purchaseOrders]);

  // Combine legacy payments with invoice payments (legacy first)
  const allPayments = [
    ...legacyPayments.map((p) => ({ ...p, id: `legacy-${p.sourceOrderId}-${p.paidAt}` })),
    ...(payments || []),
  ];

  // Helper function to get original unit price from shipment items by index
  const getOriginalUnitPrice = (index) => {
    if (!shipment?.items || index === null || index === undefined) return null;
    const shipmentItem = shipment.items[index];
    return shipmentItem ? shipmentItem.unitPrice : null;
  };

  // Helper function to get original quantity from shipment items by index
  const getOriginalQuantity = (index) => {
    if (!shipment?.items || index === null || index === undefined) return null;
    const shipmentItem = shipment.items[index];
    return shipmentItem ? shipmentItem.quantity : null;
  };

  // Helper function to add history entry for invoice changes — writes to subcollection
  const addHistoryEntry = async (field, oldValue, newValue, action, metadata = null) => {
    if (!invoice) return;

    await firestore
      .collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE)
      .doc(invoice.id)
      .collection("history")
      .add({
        changedBy: email,
        changedAt: dayjs().unix(),
        changes: [
          {
            field,
            oldValue: oldValue ?? null,
            newValue: newValue ?? null,
            action,
            metadata: metadata ?? null,
          },
        ],
      });
  };

  // Navigate away if invoice not found
  useEffect(() => {
    if (!invoiceLoading && !invoice) {
      message.error(t("invoice.not_found"));
      navigate(urls.purchaseOrderInvoicesList);
    }
  }, [invoiceLoading, invoice]);

  const handleUpdateInvoiceStatus = async (newStatus) => {
    if (!invoice) return;
    try {
      setSaving(true);
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      await addHistoryEntry("status", invoice.status, newStatus, "status_change");
      message.success("Factuurstatus bijgewerkt");
    } catch (error) {
      console.error("Error updating invoice status:", error);
      message.error("Fout bij bijwerken factuurstatus");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInvoiceNumber = async () => {
    if (!invoiceNumberValue || !invoiceNumberValue.trim()) {
      message.error("Voer een geldig factuurnummer in");
      return;
    }
    try {
      setSavingInvoiceNumber(true);
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        invoiceNumber: invoiceNumberValue.trim(),
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      await addHistoryEntry("invoiceNumber", invoice.invoiceNumber, invoiceNumberValue.trim(), "field_change", `Factuurnummer gewijzigd: ${invoice.invoiceNumber} → ${invoiceNumberValue.trim()}`);
      message.success("Factuurnummer opgeslagen");
      setEditingInvoiceNumber(false);
    } catch (error) {
      console.error("Error saving invoice number:", error);
      message.error("Fout bij opslaan factuurnummer");
    } finally {
      setSavingInvoiceNumber(false);
    }
  };

  const handleSaveInvoiceDate = async () => {
    if (!invoiceDateValue) {
      message.error("Selecteer een geldige factuurdatum");
      return;
    }
    try {
      setSavingInvoiceDate(true);
      const newDateUnix = invoiceDateValue.unix();
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        invoiceDate: newDateUnix,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      await addHistoryEntry("invoiceDate", invoice.invoiceDate, newDateUnix, "field_change", `Factuurdatum gewijzigd naar ${invoiceDateValue.format("DD-MM-YYYY")}`);
      message.success("Factuurdatum opgeslagen");
      setEditingInvoiceDate(false);
    } catch (error) {
      console.error("Error saving invoice date:", error);
      message.error("Fout bij opslaan factuurdatum");
    } finally {
      setSavingInvoiceDate(false);
    }
  };

  const handleSaveTotalAmount = async () => {
    if (totalAmountValue === null || totalAmountValue === undefined || totalAmountValue < 0) {
      message.error("Voer een geldig totaalbedrag in");
      return;
    }
    try {
      setSavingTotalAmount(true);
      const rounded = Math.round(totalAmountValue * 100) / 100;
      const newAmountDue = Math.round((rounded - (invoice.totalPaid || 0)) * 100) / 100;
      const newStatus = newAmountDue <= 0 ? "paid" : (invoice.totalPaid || 0) > 0 ? "partially_paid" : invoice.status;
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalAmount: rounded,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });
      await addHistoryEntry("totalAmount", invoice.totalAmount, rounded, "field_change", `Totaalbedrag gewijzigd: ${formatPriceByCurrency(invoice.totalAmount, invoiceCurrency)} → ${formatPriceByCurrency(rounded, invoiceCurrency)}`);
      message.success("Totaalbedrag opgeslagen");
      setEditingTotalAmount(false);
    } catch (error) {
      console.error("Error saving total amount:", error);
      message.error("Fout bij opslaan totaalbedrag");
    } finally {
      setSavingTotalAmount(false);
    }
  };

  const handleSyncToExact = async () => {
    if (!shipment?.sourceOrders?.[0]?.purchaseOrderNumber) {
      message.error("Geen ordernummer gevonden voor Exact synchronisatie");
      return;
    }

    setSyncingToExact(true);
    try {
      const idToken = await getFirebaseIDToken();
      const orderNumber = shipment.sourceOrders[0].purchaseOrderNumber;
      const endpoint = `${process.env.REACT_APP_API_URL || ""}/api/exact/purchase-orders/${orderNumber}/invoice`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken,
        },
        body: JSON.stringify({
          invoiceId: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          shipmentId: invoice.shipmentId,
          totalAmount: invoice.totalAmount,
          pdfUrl: invoice.pdfUrl || null,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data?.exactPurchaseInvoiceId) {
        await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
          exactPurchaseInvoiceId: data.exactPurchaseInvoiceId,
          exactSyncedAt: dayjs().unix(),
        });
        message.success("Factuur succesvol gesynchroniseerd met Exact");
      } else {
        message.warning("Factuur verzonden naar Exact, maar geen ID ontvangen");
      }
    } catch (error) {
      console.error("Error syncing to Exact:", error);
      message.error(`Fout bij synchroniseren met Exact: ${error.message}`);
    } finally {
      setSyncingToExact(false);
    }
  };

  const handleStartEditingInvoice = () => {
    setEditedInvoiceLines(JSON.parse(JSON.stringify(invoice.lines || [])));
    setEditedAdditionalCosts(JSON.parse(JSON.stringify(invoice.additionalCosts || [])));
    setEditedDiscountLines(JSON.parse(JSON.stringify(invoice.discountLines || [])));
    setEditingInvoice(true);
  };

  const handleCancelEditingInvoice = () => {
    setEditingInvoice(false);
    setEditedInvoiceLines([]);
    setEditedAdditionalCosts([]);
    setEditedDiscountLines([]);
  };

  const handleAddDiscountLine = () => {
    setEditedDiscountLines([
      ...editedDiscountLines,
      {
        id: `discount_${Date.now()}`,
        description: "",
        amount: 0,
        createdAt: dayjs().unix(),
        createdBy: email,
      },
    ]);
  };

  const handleRemoveDiscountLine = (discountId) => {
    setEditedDiscountLines(editedDiscountLines.filter((d) => d.id !== discountId));
  };

  const handleDiscountLineChange = (discountId, field, value) => {
    setEditedDiscountLines(
      editedDiscountLines.map((d) => {
        if (d.id !== discountId) return d;
        if (field === "amount") {
          const num = value === null || value === undefined ? 0 : -Math.abs(value);
          return { ...d, amount: num };
        }
        return { ...d, [field]: value };
      }),
    );
  };

  const handleAddInvoiceLine = () => {
    if (!selectedProduct) {
      message.error("Selecteer een product");
      return;
    }

    const newLine = {
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      productColor: selectedProduct.color,
      productCode: selectedProduct.code,
      quantity: newLineQuantity,
      unitPrice: newLineUnitPrice,
      discountPercentage: newLineDiscount,
      totalPrice: newLineQuantity * newLineUnitPrice * (1 - newLineDiscount / 100),
    };

    setEditedInvoiceLines([...editedInvoiceLines, newLine]);
    setShowAddLineModal(false);
    setSelectedProduct(null);
    setNewLineQuantity(1);
    setNewLineUnitPrice(0);
    setNewLineDiscount(0);
    message.success("Factuurregel toegevoegd");
  };

  const handleRemoveInvoiceLine = (index) => {
    const updatedLines = editedInvoiceLines.filter((_, i) => i !== index);
    setEditedInvoiceLines(updatedLines);
    message.success("Factuurregel verwijderd");
  };

  const handleOpenAddLineModal = () => {
    setSelectedProduct(null);
    setNewLineQuantity(1);
    setNewLineUnitPrice(0);
    setNewLineDiscount(0);
    setShowAddLineModal(true);
  };

  const handleCloseAddLineModal = () => {
    setShowAddLineModal(false);
    setSelectedProduct(null);
    setNewLineQuantity(1);
    setNewLineUnitPrice(0);
    setNewLineDiscount(0);
  };

  const handleProductSelect = (productId) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      setNewLineUnitPrice(product.purchasePrice || 0);
    }
  };

  const handleInvoiceLineChange = (index, field, value) => {
    const updated = [...editedInvoiceLines];
    const line = { ...updated[index] };

    if (field === "quantity" || field === "unitPrice" || field === "discountPercentage") {
      line[field] = value;
      // Recalculate totalPrice for this line
      const qty = field === "quantity" ? value : line.quantity || 0;
      const price = field === "unitPrice" ? value : line.unitPrice || 0;
      const discount = field === "discountPercentage" ? value : line.discountPercentage || 0;
      line.totalPrice = qty * price * (1 - discount / 100);
    } else {
      line[field] = value;
    }

    updated[index] = line;
    setEditedInvoiceLines(updated);
  };

  const handleAdditionalCostChange = (index, field, value) => {
    const updated = [...editedAdditionalCosts];
    updated[index] = { ...updated[index], [field]: value };
    setEditedAdditionalCosts(updated);
  };

  const handleSaveInvoiceEdits = async () => {
    try {
      setSaving(true);

      // Validate discount lines
      for (const d of editedDiscountLines) {
        if (!d.description || !d.description.trim()) {
          message.error("Geef een omschrijving voor elke kortingsregel");
          setSaving(false);
          return;
        }
        if (!d.amount || d.amount >= 0) {
          message.error("Kortingsbedragen moeten negatief zijn");
          setSaving(false);
          return;
        }
      }

      // Normalize discount amounts to be strictly negative
      const normalizedDiscountLines = editedDiscountLines.map((d) => ({
        ...d,
        amount: -Math.abs(d.amount || 0),
      }));

      // Recalculate totals
      const subtotalProducts = editedInvoiceLines.reduce((sum, line) => sum + (line.totalPrice || 0), 0);
      const subtotalAdditionalCosts = editedAdditionalCosts.reduce((sum, cost) => sum + (cost.amount || 0), 0);
      const subtotalDiscounts = normalizedDiscountLines.reduce((sum, d) => sum + (d.amount || 0), 0);
      const totalAmount = subtotalProducts + subtotalAdditionalCosts + subtotalDiscounts;
      const currentTotalPaid = invoice.totalPaid || 0;
      const amountDue = totalAmount - currentTotalPaid;

      // Track specific line changes
      const lineChanges = [];
      const oldLines = invoice.lines || [];
      const newLines = editedInvoiceLines;

      // Create maps for easier comparison (use productId instead of productCode)
      const oldLinesMap = new Map(oldLines.map((line, idx) => [line.productId, { ...line, originalIndex: idx }]));
      const newLinesMap = new Map(newLines.map((line, idx) => [line.productId, { ...line, originalIndex: idx }]));

      // Find added lines
      newLines.forEach((newLine, idx) => {
        if (!oldLinesMap.has(newLine.productId)) {
          lineChanges.push(
            `+ Regel ${idx + 1}: ${newLine.productName} (${newLine.productId}) - ${newLine.quantity}x ${formatPriceByCurrency(newLine.unitPrice, invoiceCurrency)}`,
          );
        } else {
          const oldLine = oldLinesMap.get(newLine.productId);
          // Check for changes in quantity
          if (oldLine.quantity !== newLine.quantity) {
            lineChanges.push(`~ ${newLine.productName}: aantal ${oldLine.quantity} → ${newLine.quantity}`);
          }
          // Check for changes in unit price
          if (oldLine.unitPrice !== newLine.unitPrice) {
            lineChanges.push(
              `~ ${newLine.productName}: prijs ${formatPriceByCurrency(oldLine.unitPrice, invoiceCurrency)} → ${formatPriceByCurrency(newLine.unitPrice, invoiceCurrency)}`,
            );
          }
          // Check for changes in discount
          if (oldLine.discountPercentage !== newLine.discountPercentage) {
            lineChanges.push(
              `~ ${newLine.productName}: korting ${oldLine.discountPercentage}% → ${newLine.discountPercentage}%`,
            );
          }
        }
      });

      // Find removed lines
      oldLines.forEach((oldLine) => {
        if (!newLinesMap.has(oldLine.productId)) {
          lineChanges.push(`- ${oldLine.productName} verwijderd`);
        }
      });

      // Track discount line changes
      const oldDiscountLines = invoice.discountLines || [];
      const oldDiscountMap = new Map(oldDiscountLines.map((d) => [d.id, d]));
      const newDiscountMap = new Map(normalizedDiscountLines.map((d) => [d.id, d]));
      normalizedDiscountLines.forEach((d) => {
        const old = oldDiscountMap.get(d.id);
        if (!old) {
          lineChanges.push(`+ Korting: ${d.description} (${formatPriceByCurrency(d.amount, invoiceCurrency)})`);
        } else if (old.description !== d.description || old.amount !== d.amount) {
          lineChanges.push(
            `~ Korting: ${old.description} ${formatPriceByCurrency(old.amount, invoiceCurrency)} → ${d.description} ${formatPriceByCurrency(d.amount, invoiceCurrency)}`,
          );
        }
      });
      oldDiscountLines.forEach((d) => {
        if (!newDiscountMap.has(d.id)) {
          lineChanges.push(`- Korting verwijderd: ${d.description}`);
        }
      });

      // Update invoice
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        lines: editedInvoiceLines,
        additionalCosts: editedAdditionalCosts,
        discountLines: normalizedDiscountLines,
        subtotalProducts,
        subtotalAdditionalCosts,
        subtotalDiscounts,
        totalAmount,
        amountDue,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success("Factuurwijzigingen opgeslagen");
      setEditingInvoice(false);
      setEditedInvoiceLines([]);
      setEditedAdditionalCosts([]);
      setEditedDiscountLines([]);
    } catch (error) {
      console.error("Error saving invoice edits:", error);
      message.error("Fout bij opslaan factuurwijzigingen");
    } finally {
      setSaving(false);
    }
  };

  const resetPaymentForm = () => {
    setPaymentAmount(null);
    setPaymentDate(dayjs());
    setPaymentReference("");
    setPaymentNotes("");
    setPaymentProofFile(null);
    setPaymentExchangeRate(null);
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentAmount(payment.amount);
    setPaymentDate(payment.paymentDate ? dayjs.unix(payment.paymentDate) : dayjs());
    setPaymentReference(payment.reference || "");
    setPaymentNotes(payment.notes || "");
    setPaymentProofFile(null);
    setPaymentExchangeRate(payment.exchangeRate || null);
    setShowEditPaymentModal(true);
  };

  const handleUploadPaymentProof = async (file) => {
    setUploadingPaymentProof(true);
    try {
      const storage = getStorage();
      const fileName = `payment-proofs/${invoice.id}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      setPaymentProofFile(downloadURL);
      message.success("Bewijs geüpload");
    } catch (error) {
      console.error("Error uploading proof:", error);
      message.error("Fout bij uploaden bewijs");
    } finally {
      setUploadingPaymentProof(false);
    }
  };

  const handleAddPayment = async () => {
    if (!paymentAmount || paymentAmount <= 0) {
      message.error("Voer een geldig bedrag in");
      return;
    }

    try {
      setSavingPayment(true);

      const paymentData = {
        amount: paymentAmount,
        paymentDate: paymentDate.unix(),
        reference: paymentReference,
        notes: paymentNotes,
        proofUrl: paymentProofFile,
        exchangeRate: paymentExchangeRate,
        createdAt: dayjs().unix(),
        createdBy: email,
      };

      // Add payment to invoice
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).add({
        ...paymentData,
        invoiceId: invoice.id,
      });

      // Update invoice totals (rounded to 2 decimals to avoid floating-point drift)
      const newTotalPaid = Math.round(((invoice.totalPaid || 0) + paymentAmount) * 100) / 100;
      const newAmountDue = Math.round((invoice.totalAmount - newTotalPaid) * 100) / 100;
      const newStatus = newAmountDue <= 0 ? "paid" : newTotalPaid > 0 ? "partially_paid" : invoice.status;

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success("Betaling toegevoegd");
      setShowPaymentModal(false);
      resetPaymentForm();
    } catch (error) {
      console.error("Error adding payment:", error);
      message.error("Fout bij toevoegen betaling");
    } finally {
      setSavingPayment(false);
    }
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment) return;

    try {
      setSavingPayment(true);

      const updates = {
        amount: paymentAmount,
        paymentDate: paymentDate.unix(),
        reference: paymentReference,
        notes: paymentNotes,
        exchangeRate: paymentExchangeRate,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      };

      if (paymentProofFile) {
        updates.proofUrl = paymentProofFile;
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).doc(editingPayment.id).update(updates);

      // Recalculate invoice totals (rounded to 2 decimals to avoid floating-point drift)
      const otherPayments = payments.filter((p) => p.id !== editingPayment.id);
      const otherPaymentsTotal = otherPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const newTotalPaid = Math.round((otherPaymentsTotal + paymentAmount) * 100) / 100;
      const newAmountDue = Math.round((invoice.totalAmount - newTotalPaid) * 100) / 100;
      const newStatus = newAmountDue <= 0 ? "paid" : newTotalPaid > 0 ? "partially_paid" : invoice.status;

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      const paymentChanges = [];
      if (editingPayment.amount !== paymentAmount) {
        paymentChanges.push(
          `bedrag ${formatPriceByCurrency(editingPayment.amount, invoiceCurrency)} → ${formatPriceByCurrency(paymentAmount, invoiceCurrency)}`,
        );
      }
      if ((editingPayment.reference || "") !== (paymentReference || "")) {
        paymentChanges.push(`referentie "${editingPayment.reference || "—"}" → "${paymentReference || "—"}"`);
      }
      const oldDate = editingPayment.paymentDate ? dayjs.unix(editingPayment.paymentDate).format("DD-MM-YYYY") : null;
      const newDate = paymentDate ? dayjs(paymentDate).format("DD-MM-YYYY") : null;
      if (oldDate !== newDate) {
        paymentChanges.push(`datum ${oldDate || "—"} → ${newDate || "—"}`);
      }
      if ((editingPayment.notes || "") !== (paymentNotes || "")) {
        paymentChanges.push("notitie gewijzigd");
      }
      if ((editingPayment.exchangeRate ?? null) !== (paymentExchangeRate ?? null)) {
        paymentChanges.push(`wisselkoers ${editingPayment.exchangeRate ?? "—"} → ${paymentExchangeRate ?? "—"}`);
      }
      if (paymentChanges.length > 0) {
        await addHistoryEntry(
          "payment_updated",
          editingPayment.amount,
          paymentAmount,
          "payment_updated",
          `Betaling bijgewerkt: ${paymentChanges.join(", ")}`,
        );
      }

      message.success("Betaling bijgewerkt");
      setShowEditPaymentModal(false);
      setEditingPayment(null);
      resetPaymentForm();
    } catch (error) {
      console.error("Error updating payment:", error);
      message.error("Fout bij bijwerken betaling");
    } finally {
      setSavingPayment(false);
    }
  };

  const resetCorrectionForm = () => {
    setCorrectionAmount(null);
    setCorrectionIsNegative(false);
    setCorrectionDate(dayjs());
    setCorrectionReason("");
    setCorrectionReference("");
  };

  const handleDeletePayment = async (record) => {
    try {
      setDeletingPaymentId(record.id);

      if (record.isLegacy) {
        // Remove from purchaseOrders.payments array
        const orderRef = firestore.collection("purchaseOrders").doc(record.sourceOrderId);
        const orderDoc = await orderRef.get();
        if (orderDoc.exists) {
          const currentPayments = orderDoc.data().payments || [];
          const updatedPayments = currentPayments.filter((p) => {
            // Match by paidAt timestamp and amount
            return !(p.paidAt === record.paidAt && p.amount === record.amount);
          });
          await orderRef.update({ payments: updatedPayments });
        }
      } else {
        await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).doc(record.id).delete();
      }

      // Recalculate totals from remaining non-legacy payments
      const remainingPayments = payments.filter((p) => p.id !== record.id);
      const newTotalPaid = Math.round(remainingPayments.reduce((sum, p) => sum + (p.amount || 0), 0) * 100) / 100;
      const newAmountDue = Math.round((invoice.totalAmount - newTotalPaid) * 100) / 100;
      const newStatus = newAmountDue <= 0 ? "paid" : newTotalPaid > 0 ? "partially_paid" : "submitted";

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      await addHistoryEntry(
        "payment_deleted",
        record.amount,
        null,
        "payment_deleted",
        `Betaling verwijderd: ${formatPriceByCurrency(record.amount, invoiceCurrency)}${record.isLegacy ? " (legacy)" : ""}`,
      );

      message.success("Betaling verwijderd");
    } catch (error) {
      console.error("Error deleting payment:", error);
      message.error("Fout bij verwijderen betaling");
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const handleRecalculateTotals = async () => {
    try {
      setRecalculating(true);
      const newTotalPaid = Math.round(payments.reduce((sum, p) => sum + (p.amount || 0), 0) * 100) / 100;
      const newAmountDue = Math.round((invoice.totalAmount - newTotalPaid) * 100) / 100;
      const newStatus = newAmountDue <= 0 ? "paid" : newTotalPaid > 0 ? "partially_paid" : "submitted";

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      await addHistoryEntry(
        "totals_recalculated",
        invoice.totalPaid,
        newTotalPaid,
        "totals_recalculated",
        `Totalen herberekend: betaald ${formatPriceByCurrency(invoice.totalPaid, invoiceCurrency)} → ${formatPriceByCurrency(newTotalPaid, invoiceCurrency)}`,
      );

      message.success("Totalen herberekend");
    } catch (error) {
      console.error("Error recalculating totals:", error);
      message.error("Fout bij herberekenen totalen");
    } finally {
      setRecalculating(false);
    }
  };

  const handleAddCorrection = async () => {
    if (!correctionAmount || correctionAmount <= 0) {
      message.error("Voer een geldig bedrag in");
      return;
    }
    if (!correctionReason || !correctionReason.trim()) {
      message.error("Voer een reden in voor de correctie");
      return;
    }

    try {
      setSavingCorrection(true);

      const signedAmount = correctionIsNegative ? -Math.abs(correctionAmount) : Math.abs(correctionAmount);

      const correctionData = {
        invoiceId: invoice.id,
        type: "correction",
        amount: signedAmount,
        paymentDate: correctionDate.unix(),
        reference: correctionReference || null,
        notes: correctionReason,
        createdAt: dayjs().unix(),
        createdBy: email,
      };

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE).add(correctionData);

      const newTotalPaid = Math.round(((invoice.totalPaid || 0) + signedAmount) * 100) / 100;
      const newAmountDue = Math.round((invoice.totalAmount - newTotalPaid) * 100) / 100;
      const newStatus = newAmountDue <= 0 ? "paid" : newTotalPaid > 0 ? "partially_paid" : invoice.status;

      await firestore.collection(FIRESTORE_PURCHASE_ORDER_INVOICES_TABLE).doc(invoice.id).update({
        totalPaid: newTotalPaid,
        amountDue: newAmountDue,
        status: newStatus,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      await addHistoryEntry(
        "correction_added",
        invoice.totalPaid || 0,
        newTotalPaid,
        "correction_added",
        `Correctie: ${signedAmount > 0 ? "+" : ""}${formatPriceByCurrency(signedAmount, invoiceCurrency)} — ${correctionReason}`,
      );

      message.success("Correctie toegevoegd");
      setShowCorrectionModal(false);
      resetCorrectionForm();
    } catch (error) {
      console.error("Error adding correction:", error);
      message.error("Fout bij toevoegen correctie");
    } finally {
      setSavingCorrection(false);
    }
  };

  if (invoiceLoading) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!invoice) {
    return <div style={{ padding: 24 }}>Factuur niet gevonden</div>;
  }

  // True when the sum of all purchaseOrderPayments doesn't match the stored totalPaid (indicates drift)
  const paymentsSum = Math.round(payments.reduce((sum, p) => sum + (p.amount || 0), 0) * 100) / 100;
  const storedTotalPaid = Math.round((invoice.totalPaid || 0) * 100) / 100;
  const totalsMismatch = Math.abs(paymentsSum - storedTotalPaid) >= 0.01;

  return (
    <div style={{ padding: 24 }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(urls.purchaseOrderInvoicesList)}
        style={{ marginBottom: 16 }}
      >
        Terug naar facturen
      </Button>

      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: "bold" }}>Factuur - {invoice.invoiceNumber}</span>
            <Space>
              <Tag color={invoiceStatusColors[invoice.status] || "default"}>
                {invoiceStatusLabels[invoice.status] || invoice.status || "Ingediend"}
              </Tag>
              <Select
                size="small"
                value={invoice.status || "submitted"}
                style={{ minWidth: 140 }}
                onChange={handleUpdateInvoiceStatus}
                loading={saving}
                options={Object.entries(invoiceStatusLabels).map(([value, label]) => ({ value, label }))}
              />
            </Space>
          </div>
        }
      >
        <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Factuurnummer">
            {editingInvoiceNumber ? (
              <Space size="small">
                <Input
                  size="small"
                  value={invoiceNumberValue}
                  onChange={(e) => setInvoiceNumberValue(e.target.value)}
                  onPressEnter={handleSaveInvoiceNumber}
                  style={{ width: 180 }}
                  autoFocus
                />
                <Button size="small" type="primary" loading={savingInvoiceNumber} onClick={handleSaveInvoiceNumber}>
                  Opslaan
                </Button>
                <Button size="small" onClick={() => setEditingInvoiceNumber(false)}>
                  Annuleren
                </Button>
              </Space>
            ) : (
              <Space size="small">
                <span>{invoice.invoiceNumber}</span>
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => { setInvoiceNumberValue(invoice.invoiceNumber || ""); setEditingInvoiceNumber(true); }}
                >
                  Bewerken
                </Button>
              </Space>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Factuurdatum">
            {editingInvoiceDate ? (
              <Space size="small">
                <DatePicker
                  size="small"
                  format="DD-MM-YYYY"
                  value={invoiceDateValue}
                  onChange={(date) => setInvoiceDateValue(date)}
                />
                <Button size="small" type="primary" loading={savingInvoiceDate} onClick={handleSaveInvoiceDate}>
                  Opslaan
                </Button>
                <Button size="small" onClick={() => setEditingInvoiceDate(false)}>
                  Annuleren
                </Button>
              </Space>
            ) : (
              <Space size="small">
                <span>{invoice.invoiceDate ? dayjs.unix(invoice.invoiceDate).format("DD-MM-YYYY") : "-"}</span>
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => { setInvoiceDateValue(invoice.invoiceDate ? dayjs.unix(invoice.invoiceDate) : dayjs()); setEditingInvoiceDate(true); }}
                >
                  Bewerken
                </Button>
              </Space>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Subtotaal producten">
            {formatPriceByCurrency(invoice.subtotalProducts || 0, invoiceCurrency)}
          </Descriptions.Item>
          <Descriptions.Item label="Bijkomende kosten">
            {formatPriceByCurrency(invoice.subtotalAdditionalCosts || 0, invoiceCurrency)}
          </Descriptions.Item>
          {(invoice.discountLines?.length > 0 || (invoice.subtotalDiscounts || 0) !== 0) && (
            <Descriptions.Item label="Kortingen">
              <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                {formatPriceByCurrency(invoice.subtotalDiscounts || 0, invoiceCurrency)}
              </span>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Totaalbedrag">
            {invoice.type === "proforma" && editingTotalAmount ? (
              <Space size="small">
                <InputNumber
                  size="small"
                  min={0}
                  step={0.01}
                  value={totalAmountValue}
                  onChange={(v) => setTotalAmountValue(v)}
                  style={{ width: 140 }}
                  autoFocus
                />
                <Button size="small" type="primary" loading={savingTotalAmount} onClick={handleSaveTotalAmount}>
                  Opslaan
                </Button>
                <Button size="small" onClick={() => setEditingTotalAmount(false)}>
                  Annuleren
                </Button>
              </Space>
            ) : (
              <Space size="small">
                <span style={{ fontSize: 16, fontWeight: "bold", color: "#52c41a" }}>
                  {formatPriceByCurrency(invoice.totalAmount || 0, invoiceCurrency)}
                </span>
                {invoice.type === "proforma" && (
                  <Button
                    size="small"
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => { setTotalAmountValue(invoice.totalAmount || 0); setEditingTotalAmount(true); }}
                  >
                    Bewerken
                  </Button>
                )}
              </Space>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Betaald / Openstaand">
            <span style={{ fontWeight: "bold", color: "#1890ff" }}>
              {formatPriceByCurrency(invoice.totalPaid || 0, invoiceCurrency)}
            </span>
            {" / "}
            <span style={{ fontWeight: "bold", color: (invoice.amountDue || 0) > 0 ? "#ff4d4f" : "#52c41a" }}>
              {formatPriceByCurrency(invoice.amountDue ?? invoice.totalAmount ?? 0, invoiceCurrency)}
            </span>
          </Descriptions.Item>
          {shipment ? (
            <Descriptions.Item label="Verzending">
              <Tag
                color="blue"
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`${urls.purchaseOrderShipmentDetail}${shipment.id}`)}
              >
                {shipment.shipmentNumber}
              </Tag>
            </Descriptions.Item>
          ) : (
            order && (
              <Descriptions.Item key={order.id} label="Bestelling">
                <Tag
                  color="blue"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`${urls.purchaseOrderDetail}${order.id}`)}
                >
                  {order.orderNumber}
                </Tag>
              </Descriptions.Item>
            )
          )}
          {/*<Descriptions.Item label="Exact synchronisatie">*/}
          {/*  {invoice.exactPurchaseInvoiceId ? (*/}
          {/*    <div>*/}
          {/*      {exactOutOfSync ? (*/}
          {/*        <div>*/}
          {/*          <Tag color="orange" style={{ marginBottom: 4 }}>*/}
          {/*            ⚠ Niet gesynchroniseerd met Exact*/}
          {/*          </Tag>*/}
          {/*          <div style={{ fontSize: 11, color: "#ff4d4f", marginBottom: 4 }}>*/}
          {/*            Factuur is gewijzigd na laatste synchronisatie*/}
          {/*          </div>*/}
          {/*          <Button*/}
          {/*            type="primary"*/}
          {/*            size="small"*/}
          {/*            onClick={handleSyncToExact}*/}
          {/*            loading={syncingToExact}*/}
          {/*            disabled={!shipment?.sourceOrders?.[0]?.purchaseOrderNumber}*/}
          {/*          >*/}
          {/*            Opnieuw synchroniseren*/}
          {/*          </Button>*/}
          {/*        </div>*/}
          {/*      ) : (*/}
          {/*        <Tag color="green">*/}
          {/*          ✓ Gesynchroniseerd op {dayjs.unix(invoice.exactSyncedAt).format("DD-MM-YYYY HH:mm")}*/}
          {/*        </Tag>*/}
          {/*      )}*/}
          {/*    </div>*/}
          {/*  ) : (*/}
          {/*    <Button*/}
          {/*      type="primary"*/}
          {/*      size="small"*/}
          {/*      onClick={handleSyncToExact}*/}
          {/*      loading={syncingToExact}*/}
          {/*      disabled={!shipment?.sourceOrders?.[0]?.purchaseOrderNumber}*/}
          {/*    >*/}
          {/*      Synchroniseren naar Exact*/}
          {/*    </Button>*/}
          {/*  )}*/}
          {/*</Descriptions.Item>*/}
        </Descriptions>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
          <h5 style={{ margin: 0 }}>Factuurregels</h5>
          <Space>
            {editingInvoice && (
              <Button size="small" icon={<PlusOutlined />} onClick={handleOpenAddLineModal}>
                Regel toevoegen
              </Button>
            )}
            {editingInvoice && (
              <Button size="small" icon={<PlusOutlined />} onClick={handleAddDiscountLine}>
                Kortingsregel toevoegen
              </Button>
            )}
            {!editingInvoice && (
              <Button size="small" onClick={handleStartEditingInvoice}>
                Factuur aanpassen
              </Button>
            )}
            {editingInvoice && (
              <Space>
                <Button size="small" onClick={handleCancelEditingInvoice}>
                  Annuleren
                </Button>
                <Button size="small" type="primary" onClick={handleSaveInvoiceEdits} loading={saving}>
                  Opslaan
                </Button>
              </Space>
            )}
          </Space>
        </div>

        {(editingInvoice ? editedInvoiceLines : invoice.lines || []).length > 0 && (
          <Table
            dataSource={(editingInvoice ? editedInvoiceLines : invoice.lines || []).map((l, i) => ({
              ...l,
              key: i,
              _index: i,
            }))}
            pagination={false}
            size="small"
            style={{ marginTop: 12, marginBottom: 16 }}
            columns={[
              {
                title: t("product"),
                dataIndex: "productName",
                render: (name, record) => (
                  <span>
                    {name}
                    {record.productColor && (
                      <span style={{ color: "#666", marginLeft: 4 }}>({record.productColor})</span>
                    )}
                  </span>
                ),
              },
              {
                title: t("quantity"),
                dataIndex: "quantity",
                render: (value, record) =>
                  editingInvoice ? (
                    <InputNumber
                      min={0}
                      value={value}
                      onChange={(v) => handleInvoiceLineChange(record._index, "quantity", v || 0)}
                      style={{ width: 80 }}
                    />
                  ) : (
                    (() => {
                      const originalQuantity = getOriginalQuantity(record._index);
                      const hasDiscrepancy = originalQuantity !== null && originalQuantity !== value;
                      return (
                        <div>
                          <div>{value}</div>
                          {hasDiscrepancy && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#ff4d4f",
                                marginTop: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <WarningOutlined style={{ fontSize: 10 }} />
                              <span>Origineel: {originalQuantity}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ),
              },
              {
                title: t("unit_price"),
                dataIndex: "unitPrice",
                render: (value, record) =>
                  editingInvoice ? (
                    <InputNumber
                      min={0}
                      step={0.01}
                      prefix={invoiceCurrency}
                      value={value}
                      onChange={(v) => handleInvoiceLineChange(record._index, "unitPrice", v || 0)}
                      style={{ width: 100 }}
                    />
                  ) : (
                    (() => {
                      const originalPrice = getOriginalUnitPrice(record._index);
                      const hasDiscrepancy = originalPrice !== null && Math.abs(originalPrice - (value || 0)) > 0.01;
                      return (
                        <div>
                          <div>{formatPriceByCurrency(value || 0, invoiceCurrency)}</div>
                          {hasDiscrepancy && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#ff4d4f",
                                marginTop: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <WarningOutlined style={{ fontSize: 10 }} />
                              <span>Origineel: {formatPriceByCurrency(originalPrice, invoiceCurrency)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ),
              },
              {
                title: "Korting",
                dataIndex: "discountPercentage",
                render: (value, record) =>
                  editingInvoice ? (
                    <InputNumber
                      min={0}
                      max={100}
                      value={value}
                      onChange={(v) => handleInvoiceLineChange(record._index, "discountPercentage", v || 0)}
                      style={{ width: 70 }}
                      suffix="%"
                    />
                  ) : value ? (
                    `${value}%`
                  ) : (
                    "-"
                  ),
              },
              {
                title: t("total"),
                dataIndex: "totalPrice",
                render: (v) => formatPriceByCurrency(v || 0, invoiceCurrency),
              },
              ...(editingInvoice
                ? [
                    {
                      title: "Acties",
                      dataIndex: "actions",
                      width: 80,
                      render: (_, record) => (
                        <Button
                          size="small"
                          type="link"
                          danger
                          onClick={() => handleRemoveInvoiceLine(record._index)}
                          style={{ padding: 0 }}
                        >
                          Verwijderen
                        </Button>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        )}

        {(editingInvoice ? editedAdditionalCosts : invoice.additionalCosts || []).length > 0 && (
          <>
            <h5 style={{ marginTop: 16 }}>Bijkomende kosten</h5>
            <Table
              dataSource={(editingInvoice ? editedAdditionalCosts : invoice.additionalCosts || []).map((c, i) => ({
                ...c,
                key: i,
                _index: i,
              }))}
              pagination={false}
              size="small"
              style={{ marginBottom: 16 }}
              columns={[
                { title: "Omschrijving", dataIndex: "description" },
                { title: t("quantity"), dataIndex: "quantity", render: (v) => v || 1 },
                {
                  title: t("unit_price"),
                  dataIndex: "unitPrice",
                  render: (v) => (v ? formatPriceByCurrency(v, invoiceCurrency) : "-"),
                },
                {
                  title: t("total"),
                  dataIndex: "amount",
                  render: (value, record) =>
                    editingInvoice ? (
                      <InputNumber
                        min={0}
                        step={0.01}
                        prefix={invoiceCurrency}
                        value={value}
                        onChange={(v) => handleAdditionalCostChange(record._index, "amount", v || 0)}
                        style={{ width: 100 }}
                      />
                    ) : (
                      formatPriceByCurrency(value || 0, invoiceCurrency)
                    ),
                },
              ]}
            />
          </>
        )}

        {(editingInvoice ? editedDiscountLines : invoice.discountLines || []).length > 0 && (
          <>
            <h5 style={{ marginTop: 16 }}>Kortingsregels</h5>
            <Table
              dataSource={(editingInvoice ? editedDiscountLines : invoice.discountLines || []).map((d, i) => ({
                ...d,
                key: d.id || i,
              }))}
              pagination={false}
              size="small"
              style={{ marginBottom: 16 }}
              columns={[
                {
                  title: "Omschrijving",
                  dataIndex: "description",
                  render: (value, record) =>
                    editingInvoice ? (
                      <Input
                        value={value}
                        placeholder="bv. Volumekorting"
                        onChange={(e) => handleDiscountLineChange(record.id, "description", e.target.value)}
                      />
                    ) : (
                      value || "-"
                    ),
                },
                {
                  title: "Bedrag",
                  dataIndex: "amount",
                  width: 200,
                  render: (value, record) =>
                    editingInvoice ? (
                      <InputNumber
                        min={0}
                        step={0.01}
                        prefix={invoiceCurrency}
                        value={Math.abs(value)}
                        onChange={(v) =>
                          handleDiscountLineChange(
                            record.id,
                            "amount",
                            v === null || v === undefined ? 0 : -Math.abs(v),
                          )
                        }
                        style={{ width: 150 }}
                      />
                    ) : (
                      <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
                        {formatPriceByCurrency(value || 0, invoiceCurrency)}
                      </span>
                    ),
                },
                ...(editingInvoice
                  ? [
                      {
                        title: "Acties",
                        dataIndex: "actions",
                        width: 80,
                        render: (_, record) => (
                          <Button
                            size="small"
                            type="link"
                            danger
                            onClick={() => handleRemoveDiscountLine(record.id)}
                            style={{ padding: 0 }}
                          >
                            Verwijderen
                          </Button>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </>
        )}

        {invoice.pdfUrl && (
          <div style={{ marginTop: 8 }}>
            <a href={invoice.pdfUrl} target="_blank" rel="noopener noreferrer">
              <Button type="primary" size="small">
                Download factuur PDF
              </Button>
            </a>
          </div>
        )}

        {invoice.remarks && (
          <div style={{ marginTop: 12, padding: 8, backgroundColor: "#fff", borderRadius: 4 }}>
            <strong>Opmerkingen:</strong>
            <div style={{ marginTop: 4, color: "#666" }}>{invoice.remarks}</div>
          </div>
        )}
      </Card>

      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Space>
              <span style={{ fontWeight: "bold" }}>Betalingen</span>
              {totalsMismatch && (
                <Tooltip
                  title={
                    <div>
                      <div>Som betalingen: <strong>{formatPriceByCurrency(paymentsSum, invoiceCurrency)}</strong></div>
                      <div>Opgeslagen betaald: <strong>{formatPriceByCurrency(storedTotalPaid, invoiceCurrency)}</strong></div>
                      <div style={{ marginTop: 4, color: "#ffccc7" }}>Verschil: <strong>{formatPriceByCurrency(Math.abs(paymentsSum - storedTotalPaid), invoiceCurrency)}</strong></div>
                    </div>
                  }
                >
                  <Tag color="orange" style={{ marginLeft: 4, cursor: "help" }}>
                    <WarningOutlined style={{ marginRight: 4 }} />
                    Totalen komen niet overeen
                  </Tag>
                </Tooltip>
              )}
            </Space>
            <Space>
              {totalsMismatch && (
                <Button
                  icon={<ReloadOutlined />}
                  size="small"
                  danger
                  onClick={handleRecalculateTotals}
                  loading={recalculating}
                >
                  Totalen herberekenen
                </Button>
              )}
              <Button
                icon={<PlusOutlined />}
                size="small"
                onClick={() => {
                  resetCorrectionForm();
                  setShowCorrectionModal(true);
                }}
              >
                Correctie toevoegen
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={() => {
                  resetPaymentForm();
                  setPaymentAmount(invoice.amountDue > 0 ? invoice.amountDue : null);
                  setShowPaymentModal(true);
                }}
              >
                Betaling toevoegen
              </Button>
            </Space>
          </div>
        }
      >
        {/* Payments Section */}
        <div>
          {allPayments.length === 0 ? (
            <div
              style={{
                padding: 16,
                backgroundColor: "#fafafa",
                borderRadius: 8,
                textAlign: "center",
                color: "#999",
              }}
            >
              Nog geen betalingen geregistreerd
            </div>
          ) : (
            <Table
              dataSource={allPayments.map((p, i) => ({ ...p, key: i }))}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "Datum",
                  dataIndex: "paymentDate",
                  render: (v, record) => {
                    const date = v || record.paidAt || record.date;
                    return date ? dayjs.unix(date).format("DD-MM-YYYY") : "-";
                  },
                },
                {
                  title: "Type",
                  dataIndex: "type",
                  width: 150,
                  render: (v, record) => {
                    if (record.isLegacy) return <Tag color="default">Legacy</Tag>;
                    return (
                      <Space size={4} wrap>
                        {v === "correction" ? (
                          record.amount >= 0 ? (
                            <Tag color="cyan">Creditcorrectie</Tag>
                          ) : (
                            <Tag color="orange">Debetcorrectie</Tag>
                          )
                        ) : (
                          <Tag color="green">Betaling</Tag>
                        )}
                        {record.migratedFromLegacy && <Tag color="purple">Gemigreerd</Tag>}
                      </Space>
                    );
                  },
                },
                {
                  title: "Bedrag",
                  dataIndex: "amount",
                  render: (v, record) => {
                    const isCorrection = record.type === "correction";
                    const isNegative = (v || 0) < 0;
                    const color = isCorrection ? (isNegative ? "#fa8c16" : "#13c2c2") : "#52c41a";
                    return (
                      <div>
                        <strong style={{ color }}>{formatPriceByCurrency(v || 0, invoiceCurrency)}</strong>
                        {record.exchangeRate && (
                          <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                            €{(v * record.exchangeRate).toFixed(2)} @ {record.exchangeRate}
                          </div>
                        )}
                        {record.isLegacy && (
                          <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                            Aanbetaling via {record.sourceOrderNumber}
                          </div>
                        )}
                      </div>
                    );
                  },
                },
                {
                  title: "Referentie",
                  dataIndex: "reference",
                  render: (v, record) => {
                    if (record.isLegacy) {
                      return record.sourceOrderNumber || "-";
                    }
                    return v || "-";
                  },
                },
                {
                  title: "Notities",
                  dataIndex: "notes",
                  render: (v, record) => {
                    const notes = v || record.notes || "";
                    if (record.isLegacy && !notes) {
                      return "Aanbetaling via inkooporder";
                    }
                    return notes || "-";
                  },
                },
                {
                  title: "Bewijs",
                  dataIndex: "proofUrl",
                  render: (v, record) => {
                    const proofUrl = v || record.proofUrl;
                    return proofUrl ? (
                      <a href={proofUrl} target="_blank" rel="noopener noreferrer">
                        <Button size="small" type="link" style={{ padding: 0 }}>
                          Bekijk
                        </Button>
                      </a>
                    ) : (
                      "-"
                    );
                  },
                },
                {
                  title: "Door",
                  dataIndex: "createdBy",
                  render: (v, record) => {
                    const createdBy = v || record.createdBy || record.paidBy;
                    return <span style={{ fontSize: 11, color: "#888" }}>{createdBy || "-"}</span>;
                  },
                },
                {
                  title: "Acties",
                  dataIndex: "actions",
                  render: (_, record) => (
                    <Space size={2} direction="vertical">
                      {!record.isLegacy && (
                        <Button
                          size="small"
                          type="link"
                          onClick={() => handleEditPayment(record)}
                          style={{ padding: 0 }}
                        >
                          Bewerken
                        </Button>
                      )}
                      <Popconfirm
                        title="Betaling verwijderen?"
                        description={`${formatPriceByCurrency(record.amount, invoiceCurrency)}${record.isLegacy ? " (legacy uit inkooporder)" : ""} wordt verwijderd en de factuur wordt herberekend.`}
                        onConfirm={() => handleDeletePayment(record)}
                        okText="Verwijderen"
                        cancelText="Annuleren"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          size="small"
                          type="link"
                          danger
                          loading={deletingPaymentId === record.id}
                          style={{ padding: 0 }}
                        >
                          Verwijderen
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </div>
      </Card>

      {/* Geschiedenis (history subcollection) */}
      <Card size="small" style={{ marginTop: 16 }}>
        <Collapse>
          <Panel
            header={
              <span>
                Geschiedenis{" "}
                {!historyLoading && invoiceHistory.length > 0 && (
                  <Tag style={{ marginLeft: 4 }}>{invoiceHistory.length}</Tag>
                )}
              </span>
            }
            key="history"
          >
            {historyLoading ? (
              <div style={{ textAlign: "center", padding: 24 }}>
                <Spin />
              </div>
            ) : invoiceHistory.length === 0 ? (
              <div style={{ color: "#999", padding: 8 }}>Nog geen geschiedenis beschikbaar.</div>
            ) : (
              <div style={{ maxHeight: 500, overflowY: "auto", paddingTop: 8 }}>
                <Timeline>
                  {invoiceHistory.map((entry) => (
                    <Timeline.Item key={entry.id}>
                      <div style={{ marginBottom: 4 }}>
                        <span style={{ fontWeight: 500 }}>{entry.changedBy}</span>
                        <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>
                          {dayjs.unix(entry.changedAt).format("DD-MM-YYYY HH:mm")}
                        </span>
                      </div>
                      {entry.changes?.map((change, idx) => {
                        const action = change.action || change.field;
                        if (action === "invoice_created") {
                          return (
                            <div key={idx} style={{ fontSize: 13, color: "#52c41a" }}>
                              ✓ Factuur aangemaakt
                            </div>
                          );
                        }
                        if (action === "status_change" || change.field === "status") {
                          return (
                            <div key={idx} style={{ fontSize: 13, marginBottom: 6 }}>
                              Status: <Tag>{invoiceStatusLabels[change.oldValue] || change.oldValue}</Tag>
                              <ArrowRightOutlined style={{ marginRight: 8 }} />
                              <Tag color="blue">{invoiceStatusLabels[change.newValue] || change.newValue}</Tag>
                            </div>
                          );
                        }
                        if (action === "payment_added" || change.field === "payment_added") {
                          return (
                            <div key={idx} style={{ fontSize: 13, color: "#1890ff" }}>
                              + Betaling toegevoegd
                              {change.metadata && (
                                <span style={{ color: "#555", marginLeft: 6 }}>{change.metadata}</span>
                              )}
                            </div>
                          );
                        }
                        if (action === "payment_updated" || change.field === "payment_updated") {
                          return (
                            <div key={idx} style={{ fontSize: 13 }}>
                              Betaling bijgewerkt
                              {change.metadata && (
                                <span style={{ color: "#555", marginLeft: 6 }}>{change.metadata}</span>
                              )}
                            </div>
                          );
                        }
                        if (action === "correction_added" || change.field === "correction_added") {
                          return (
                            <div key={idx} style={{ fontSize: 13, color: "#13c2c2" }}>
                              ± Correctie geboekt
                              {change.metadata && (
                                <span style={{ color: "#555", marginLeft: 6 }}>{change.metadata}</span>
                              )}
                            </div>
                          );
                        }
                        if (action === "payment_deleted" || change.field === "payment_deleted") {
                          return (
                            <div key={idx} style={{ fontSize: 13, color: "#ff4d4f" }}>
                              − Betaling verwijderd
                              {change.metadata && (
                                <span style={{ color: "#555", marginLeft: 6 }}>{change.metadata}</span>
                              )}
                            </div>
                          );
                        }
                        if (action === "totals_recalculated" || change.field === "totals_recalculated") {
                          return (
                            <div key={idx} style={{ fontSize: 13, color: "#722ed1" }}>
                              ↻ Totalen herberekend
                              {change.metadata && (
                                <span style={{ color: "#555", marginLeft: 6 }}>{change.metadata}</span>
                              )}
                            </div>
                          );
                        }
                        if (action === "invoice_edit" || change.action === "invoice_edit") {
                          const iconMap = {
                            line_added: { color: "#52c41a", prefix: "+" },
                            line_removed: { color: "#ff4d4f", prefix: "−" },
                            line_quantity: { color: "#1890ff", prefix: "~" },
                            line_price: { color: "#1890ff", prefix: "~" },
                            line_discount: { color: "#faad14", prefix: "~" },
                            cost_added: { color: "#52c41a", prefix: "+" },
                            cost_removed: { color: "#ff4d4f", prefix: "−" },
                            cost_changed: { color: "#1890ff", prefix: "~" },
                            discount_added: { color: "#52c41a", prefix: "+" },
                            discount_removed: { color: "#ff4d4f", prefix: "−" },
                            discount_changed: { color: "#1890ff", prefix: "~" },
                            total_amount: { color: "#888", prefix: "Σ" },
                          };
                          const style = iconMap[change.field] || { color: "#555", prefix: "~" };
                          return (
                            <div key={idx} style={{ fontSize: 13, color: style.color, marginBottom: 2 }}>
                              <span style={{ marginRight: 5, fontWeight: "bold" }}>{style.prefix}</span>
                              {change.metadata || change.field}
                            </div>
                          );
                        }
                        return (
                          <div key={idx} style={{ fontSize: 12, color: "#888" }}>
                            {change.field}
                            {change.oldValue != null && (
                              <>
                                : {JSON.stringify(change.oldValue)} <ArrowRightOutlined style={{ marginRight: 8 }} />{" "}
                                {JSON.stringify(change.newValue)}
                              </>
                            )}
                            {change.oldValue == null && change.newValue != null && (
                              <span>: {JSON.stringify(change.newValue)}</span>
                            )}
                          </div>
                        );
                      })}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </div>
            )}
          </Panel>
        </Collapse>
      </Card>

      {/* Add Payment Modal */}
      <Modal
        title="Betaling toevoegen"
        open={showPaymentModal}
        onOk={handleAddPayment}
        onCancel={() => {
          setShowPaymentModal(false);
          resetPaymentForm();
        }}
        okText="Toevoegen"
        cancelText="Annuleren"
        confirmLoading={savingPayment}
        okButtonProps={{ disabled: uploadingPaymentProof }}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bedrag ({invoiceCurrency}):</label>
            <InputNumber
              min={0}
              step={0.01}
              value={paymentAmount}
              onChange={setPaymentAmount}
              style={{ width: "100%" }}
              prefix={invoiceCurrency}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Datum:</label>
            <DatePicker value={paymentDate} onChange={setPaymentDate} style={{ width: "100%" }} format="DD-MM-YYYY" />
          </div>
          {invoiceCurrency !== "EUR" && (
            <div>
              <label style={{ display: "block", marginBottom: 4 }}>Wisselkoers (naar EUR):</label>
              <InputNumber
                min={0}
                step={0.0001}
                value={paymentExchangeRate}
                onChange={setPaymentExchangeRate}
                style={{ width: "100%" }}
                placeholder="Bijv. 0.92"
              />
            </div>
          )}
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Referentie:</label>
            <Input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Notities:</label>
            <TextArea value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} rows={3} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bewijs (PDF/afbeelding):</label>
            <Upload
              beforeUpload={(file) => {
                handleUploadPaymentProof(file);
                return false;
              }}
              onRemove={() => setPaymentProofFile(null)}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Upload bewijs</Button>
            </Upload>
          </div>
        </Space>
      </Modal>

      {/* Edit Payment Modal */}
      <Modal
        title="Betaling bewerken"
        open={showEditPaymentModal}
        onOk={handleUpdatePayment}
        onCancel={() => {
          setShowEditPaymentModal(false);
          setEditingPayment(null);
          resetPaymentForm();
        }}
        okText="Opslaan"
        cancelText="Annuleren"
        confirmLoading={savingPayment}
        okButtonProps={{ disabled: uploadingPaymentProof }}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bedrag ({invoiceCurrency}):</label>
            <InputNumber
              min={0}
              step={0.01}
              value={paymentAmount}
              onChange={setPaymentAmount}
              style={{ width: "100%" }}
              prefix={invoiceCurrency}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Datum:</label>
            <DatePicker value={paymentDate} onChange={setPaymentDate} style={{ width: "100%" }} format="DD-MM-YYYY" />
          </div>
          {invoiceCurrency !== "EUR" && (
            <div>
              <label style={{ display: "block", marginBottom: 4 }}>Wisselkoers (naar EUR):</label>
              <InputNumber
                min={0}
                step={0.0001}
                value={paymentExchangeRate}
                onChange={setPaymentExchangeRate}
                style={{ width: "100%" }}
                placeholder="Bijv. 0.92"
              />
            </div>
          )}
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Referentie:</label>
            <Input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Notities:</label>
            <TextArea value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} rows={3} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bewijs (PDF/afbeelding):</label>
            <Upload
              beforeUpload={(file) => {
                handleUploadPaymentProof(file);
                return false;
              }}
              onRemove={() => setPaymentProofFile(null)}
              maxCount={1}
              defaultFileList={
                editingPayment?.proofUrl
                  ? [{ uid: "1", name: "Bewijs", status: "done", url: editingPayment.proofUrl }]
                  : []
              }
            >
              <Button icon={<UploadOutlined />}>Upload bewijs</Button>
            </Upload>
          </div>
        </Space>
      </Modal>

      {/* Correction Modal */}
      <Modal
        title="Correctie toevoegen"
        open={showCorrectionModal}
        onOk={handleAddCorrection}
        onCancel={() => {
          setShowCorrectionModal(false);
          resetCorrectionForm();
        }}
        okText="Toevoegen"
        cancelText="Annuleren"
        confirmLoading={savingCorrection}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div
            style={{
              padding: "8px 12px",
              backgroundColor: "#fffbe6",
              border: "1px solid #ffe58f",
              borderRadius: 6,
              fontSize: 13,
              color: "#ad6800",
            }}
          >
            Gebruik correcties om historische betalingsverschillen recht te trekken. Een{" "}
            <strong>creditcorrectie</strong> verhoogt het betaalde bedrag (bijv. een betaling die elders geboekt staat
            maar voor deze factuur was). Een <strong>debetcorrectie</strong> verlaagt het betaalde bedrag (bijv. een
            deel van een betaling op deze factuur die eigenlijk voor een andere factuur was).
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Type correctie:</label>
            <Select
              value={correctionIsNegative ? "debet" : "credit"}
              onChange={(v) => setCorrectionIsNegative(v === "debet")}
              style={{ width: "100%" }}
              options={[
                { value: "credit", label: "Creditcorrectie (+) — verhoogt het betaalde bedrag" },
                { value: "debet", label: "Debetcorrectie (−) — verlaagt het betaalde bedrag" },
              ]}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Bedrag ({invoiceCurrency}):</label>
            <InputNumber
              min={0}
              step={0.01}
              value={correctionAmount}
              onChange={setCorrectionAmount}
              style={{ width: "100%" }}
              prefix={correctionIsNegative ? `−${invoiceCurrency}` : `+${invoiceCurrency}`}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Datum:</label>
            <DatePicker
              value={correctionDate}
              onChange={setCorrectionDate}
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>
              Reden <span style={{ color: "#ff4d4f" }}>*</span>:
            </label>
            <TextArea
              value={correctionReason}
              onChange={(e) => setCorrectionReason(e.target.value)}
              rows={3}
              placeholder="Bijv: Deel van betaling PO-2024-015 betrof deze factuur"
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Referentie (optioneel):</label>
            <Input
              value={correctionReference}
              onChange={(e) => setCorrectionReference(e.target.value)}
              placeholder="Bijv: PO-2024-015 of bankreferentie"
            />
          </div>
          {correctionAmount > 0 && (
            <div
              style={{
                padding: "8px 12px",
                backgroundColor: correctionIsNegative ? "#fff7e6" : "#f6ffed",
                border: `1px solid ${correctionIsNegative ? "#ffd591" : "#b7eb8f"}`,
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              Na correctie:{" "}
              <strong>
                {formatPriceByCurrency(
                  (invoice.totalPaid || 0) +
                    (correctionIsNegative ? -Math.abs(correctionAmount) : Math.abs(correctionAmount)),
                  invoiceCurrency,
                )}
              </strong>{" "}
              betaald, openstaand:{" "}
              <strong>
                {formatPriceByCurrency(
                  invoice.totalAmount -
                    ((invoice.totalPaid || 0) +
                      (correctionIsNegative ? -Math.abs(correctionAmount) : Math.abs(correctionAmount))),
                  invoiceCurrency,
                )}
              </strong>
            </div>
          )}
        </Space>
      </Modal>

      {/* Add Line Modal */}
      <Modal
        title="Factuurregel toevoegen"
        open={showAddLineModal}
        onOk={handleAddInvoiceLine}
        onCancel={handleCloseAddLineModal}
        okText="Toevoegen"
        cancelText="Annuleren"
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Product:</label>
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder="Selecteer een product"
              value={selectedProduct?.id}
              onChange={handleProductSelect}
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              options={products.map((p) => ({
                value: p.id,
                label: `${p.name}${p.color ? ` (${p.color})` : ""} - ${p.code || ""}`,
              }))}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Aantal:</label>
            <InputNumber min={0} value={newLineQuantity} onChange={setNewLineQuantity} style={{ width: "100%" }} />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Prijs per stuk ({invoiceCurrency}):</label>
            <InputNumber
              min={0}
              step={0.01}
              value={newLineUnitPrice}
              onChange={setNewLineUnitPrice}
              style={{ width: "100%" }}
              prefix={invoiceCurrency}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: 4 }}>Korting (%):</label>
            <InputNumber
              min={0}
              max={100}
              value={newLineDiscount}
              onChange={setNewLineDiscount}
              style={{ width: "100%" }}
              suffix="%"
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default PurchaseOrderInvoiceDetail;
